<template>
  <h1 class="sr-only">{{ exhibition ? exhibition.title : '전시' }}</h1>
  <!-- s::PAGE -->
  <div v-if="loading" class="loading"></div>

  <div v-else-if="error" class="error">{{ error.message }}</div>

  <div v-else class="contents">
    <!-- s:: ARTICLE -->
    <div class="article">
      <div class="layout">
        <div class="box">
          <ArticleSnb/>
          <div v-if="exhibition" class="content-panel">
            <div class="view-type1">
              <ArticleButtons v-model:isLike="exhibition.isLike" :uuid="exhibition.uuid" :likeType="exhibition.likeType"/>
              <div class="img">
                <img :src="exhibition.imageUrl" :alt="exhibition.title || '전시 포스터'">
              </div>
              <div class="info-box">
                <h3>{{ exhibition.title }} </h3>
                <dl>
                  <dt v-if="exhibition.periodFrom && exhibition.periodTo" style="position: relative">기간
                    <StatusBadge
                                 :period-from="exhibition.periodFrom"
                                 :period-to="exhibition.periodTo"
                                 wrap-class="detail"
                    />
                  </dt>
                  <dd>{{ getPeriod() }}</dd>
                  <template v-if="getTime()">
                    <dt>시간</dt>
                    <dd>{{ getTime() }}</dd>
                  </template>
                  <template v-if="exhibition.location">
                    <dt>
                      장소
                      <a @click="openKakaoMap(exhibition.location)">
                        <img src="@/assets/images/icon/ico_map_pin.svg" alt="카카오맵"
                             style="width: clamp(16px, 1.4vw, 18px); cursor: pointer; margin-bottom: -2px;"/>
                      </a>
                    </dt>
                    <dd>{{ exhibition.location }} {{ exhibition.locationDetail }}</dd>
                  </template>
                  <template v-if="exhibition.contactPhoneNum || exhibition.contactEmail">
                    <dt>문의</dt>
                    <dd>{{ exhibition.contactPhoneNum }}</dd>
                    <dd v-if="exhibition.contactEmail">{{ exhibition.contactEmail }}</dd>
                  </template>
                  <template v-if="exhibition.content">
                    <dd>
                      <MimioContent :html="exhibition.content"/>
                    </dd>
                  </template>
                  <template v-if="exhibition.originLink">
                    <dt>바로가기</dt>
                    <dd>
                      <a :href="exhibition.originLink" target="_blank" class="link" rel="noopener noreferrer" >{{ exhibition.originLink }}</a>
                    </dd>
                  </template>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- e:: ARTICLE -->
  </div>
  <!-- e::PAGE -->
</template>

<script setup>
import {onMounted, ref} from "vue";
import {useRoute} from "vue-router";
import {apiClient} from "@/services/auth-header";
import ArticleSnb from "@/components/snb/ArticleSnb.vue";
import ArticleButtons from "@/components/article/ArticleButtons.vue";
import {useUiStore} from "@/store/useUiStore";
import StatusBadge from "@/components/article/StatusBadge.vue";
import MimioContent from "@/components/common/MimioContent.vue";
import { useAnalytics } from "@/composables/useAnalytics";
import { setSeoMeta, excerpt, DETAIL_FALLBACK_DESCRIPTION } from "@/composables/useSeoMeta";

const uiStore = useUiStore();
const { trackArticleView } = useAnalytics();
const route = useRoute();
const articleUuid = ref(route.params.uuid);
const loading = ref(true);
const error = ref(null);
const exhibition = ref(null);

const getPeriod = () => {
  let period = exhibition.value.periodFrom + ' ~ ' + exhibition.value.periodTo;
  if (exhibition.value.periodMemo) {
    period += ' (' + exhibition.value.periodMemo + ')';
  }
  return period;
}

const getTime = () => {
  let time = '';
  if (exhibition.value.timeFrom) {
    time += exhibition.value.timeFrom;
  }

  if (exhibition.value.timeTo) {
    time += ' ~ ' + exhibition.value.timeTo;
  }

  if (exhibition.value.timeMemo) {
    time += ' (' + exhibition.value.timeMemo + ')';
  }

  return time;
}

const openKakaoMap = (address) => {
  if (!address) {
    return;
  }
  const encodedAddress = encodeURIComponent(address);
  const kakaoMapUrl = `https://map.kakao.com/?q=${encodedAddress}`;
  window.open(kakaoMapUrl, "_blank");
};

onMounted(async () => {
  try {
    uiStore.showLoading();
    const response = await apiClient.get(`/v1/articles/exhibitions/${articleUuid.value}`);
    exhibition.value = response.data.data;

    // 페이지별 SEO 메타 (mimio#59)
    setSeoMeta({
      title: `${exhibition.value.title} | mimio`,
      description:
        excerpt(exhibition.value.content) || DETAIL_FALLBACK_DESCRIPTION.exhibition,
      image: exhibition.value.imageUrl,
      type: "article",
    });
    trackArticleView({ ...exhibition.value, id: exhibition.value.uuid }, "exhibition");
  } catch (error) {
    error.value = error; // 에러를 저장합니다.
  } finally {
    loading.value = false; // 작업이 끝난 후에는 로딩 상태를 끝냅니다.
    uiStore.hideLoading();
  }
});
</script>